import { Add, CloudDownload } from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import MultiPatientConflictDialog from '~/components/Dialogs/MultiPatientConflictDialog';
import { MAX_FILES } from '~/components/DicomLoad/LocalLoad/LocalFilesTab';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { sessionInitStatusAtom } from '~/state/atoms/sessionAtoms';
import { dicomSourcesModalOpenState } from '~/state/atoms/viewerAtoms';
import AiAssistantButton from '../../AiAssistantButton';
import StepHeader from '../StepHeader';
import DatasetList from './DatasetList';

export default function SetupContent() {
    const { enqueueSnackbar } = useSnackbar();
    const setDicomModalOpen = useSetRecoilState(dicomSourcesModalOpenState);
    const { initializeEnvironment } = useViewportEnvironment();
    const { processImportedFiles, patientConflict, resolvePatientConflict } =
        useProcessDicomFiles();
    const folderInputRef = React.useRef<HTMLInputElement | null>(null);
    const { sessionId } = useParams<{ sessionId?: string }>();
    const sessionInitStatus = useRecoilValue(sessionInitStatusAtom);
    const isSessionMode = !!sessionId;

    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(
        null
    );
    const addMenuOpen = Boolean(addMenuAnchor);
    const handleOpenAddMenu = useCallback(
        (e: React.MouseEvent<HTMLElement>) => setAddMenuAnchor(e.currentTarget),
        []
    );
    const handleCloseAddMenu = useCallback(() => setAddMenuAnchor(null), []);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target.files && e.target.files[0]) {
                const files = Array.from(e.target.files);
                e.target.value = '';
                if (files.length > MAX_FILES) {
                    enqueueSnackbar(
                        `Loading canceled: too many files selected. Limit is ${MAX_FILES}.`,
                        { variant: 'error' }
                    );
                    return;
                }
                const processedItems = await processImportedFiles(files);
                if (processedItems) {
                    initializeEnvironment(processedItems);
                }
            }
        },
        [processImportedFiles, initializeEnvironment, enqueueSnackbar]
    );

    const handleLoadFolder = useCallback(() => {
        folderInputRef.current?.click();
    }, []);

    const openDicomModal = useCallback(
        () => setDicomModalOpen(true),
        [setDicomModalOpen]
    );

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box flexShrink={0}>
                <StepHeader titleId="workflow.setup" />

                <Box
                    px={1.5}
                    my={1}
                    mb={1.5}
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleOpenAddMenu}
                        data-testid="addDataButton"
                        sx={{
                            textTransform: 'capitalize',
                            fontSize: 12,
                        }}
                    >
                        <Add
                            sx={{
                                position: 'absolute',
                                left: 8,
                                fontSize: 16,
                            }}
                        />
                        <FormattedMessage id="addData" />
                    </Button>
                    <Menu
                        anchorEl={addMenuAnchor}
                        open={addMenuOpen}
                        onClose={handleCloseAddMenu}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: addMenuAnchor?.offsetWidth ?? 'auto',
                                },
                            },
                        }}
                    >
                        <MenuItem
                            component="label"
                            htmlFor="loadDicomInput"
                            sx={{ fontSize: 13 }}
                        >
                            <ListItemIcon>
                                <FileUploadIcon sx={{ fontSize: 14 }} />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="loadFiles" />
                            </ListItemText>
                            <input
                                id="loadDicomInput"
                                data-testid="loadDicomInput"
                                onChange={(e) => {
                                    handleCloseAddMenu();
                                    handleFileChange(e);
                                }}
                                type="file"
                                hidden
                                multiple
                                accept=".dcm,.dicom,.dc3,application/dicom,.gz,.dcm.gz"
                            />
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleCloseAddMenu();
                                handleLoadFolder();
                            }}
                            data-testid="loadFolderButton"
                            sx={{ fontSize: 13 }}
                        >
                            <ListItemIcon>
                                <FolderOutlinedIcon sx={{ fontSize: 14 }} />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="loadFolder" />
                            </ListItemText>
                        </MenuItem>
                        {!isSessionMode && (
                            <MenuItem
                                onClick={() => {
                                    handleCloseAddMenu();
                                    openDicomModal();
                                }}
                                sx={{ fontSize: 13 }}
                            >
                                <ListItemIcon>
                                    <CloudDownload sx={{ fontSize: 14 }} />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="loadFromDicom" />
                                </ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                    <input
                        ref={folderInputRef}
                        type="file"
                        hidden
                        multiple
                        // @ts-expect-error webkitdirectory is a non-standard but widely supported attribute
                        webkitdirectory="true"
                        onChange={handleFileChange}
                        accept=".dcm,.dicom,.dc3,application/dicom,.gz,.dcm.gz"
                    />
                    <AiAssistantButton />
                </Box>

                <Divider />
            </Box>

            <Box flex={1} overflow="auto" minHeight={0}>
                {sessionInitStatus.isLoading ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        px={1.5}
                        py={1}
                    >
                        <CircularProgress size={16} />
                        <Typography fontSize={12} color="text.secondary">
                            <FormattedMessage id="downloadingSession" />
                        </Typography>
                    </Box>
                ) : (
                    <DatasetList />
                )}
            </Box>

            <MultiPatientConflictDialog
                open={patientConflict.open}
                loadedPatientId={patientConflict.loadedPatientId}
                incomingPatientIds={patientConflict.incomingPatientIds}
                hasUnsavedChanges={patientConflict.hasUnsavedChanges}
                clinicalUse={patientConflict.clinicalUse}
                onAction={resolvePatientConflict}
            />
        </Box>
    );
}
